import { getEnv, types, Instance } from "mobx-state-tree";
import { StorageModel } from "../storage/StorageModel";
import { endpoints } from "../api/ApiEndpoints";
import { api } from "../api/ApiService";
import { ActivityTypes, ActivityPriceType } from "./ActivityPricesStorage";

// One billed line as actually charged for a file (a UserActivityLog price snapshot):
// gross list price, discount applied, net credits billed, and the price type. A file's
// rows sum to exactly what was charged; only SUCCESSFUL runs have a row (failed runs are
// never charged), so a service with no row here was not charged / is free.
export interface OrderBreakdownRow {
    activityTypeId: ActivityTypes;
    service: string;
    grossPrice: number;
    discount: number;
    credits: number;
    priceType: ActivityPriceType;
    timestamp: string;
}

const OrderBreakdownModel = types.model({
    fileId: types.optional(types.number, 0),
    rows: types.optional(types.frozen<OrderBreakdownRow[]>(), []),
});

export const OrderBreakdownStorageModel = types
    .compose(OrderBreakdownModel, StorageModel)
    .named("OrderBreakdownStorageModel")
    .actions((self) => ({
        mapResponse(response: OrderBreakdownRow[]) {
            self.rows = response ?? [];
        },
        setFileId(fileId: number) {
            self.fileId = fileId;
        },
    }))
    .actions((self) => ({
        // Point the store at a file and (re)fetch its charged rows. Called by the page
        // when the loaded file changes and after a run completes (so credits refresh live).
        loadForFile(fileId: number) {
            self.setFileId(fileId);
            return self.load();
        },
        clear() {
            self.setFileId(0);
            self.rows = [];
        },
    }))
    .views((self) => ({
        get canLoadItems() {
            return self.fileId > 0;
        },
        get endpoint() {
            const endpoint = endpoints.get("shop_order_breakdown");
            endpoint.routeParams = [self.fileId];
            return endpoint;
        },
        // Total net credits actually billed for the file (sum of every charged row).
        get totalCredits() {
            return self.rows.reduce((sum, r) => sum + r.credits, 0);
        },
        // Net credits charged for a single service (0 when it was not charged / failed).
        getCreditsForActivity(activityTypeId: ActivityTypes) {
            return self.rows
                .filter((r) => r.activityTypeId === activityTypeId)
                .reduce((sum, r) => sum + r.credits, 0);
        },
        // Whether the file was charged at all for a given service.
        wasChargedFor(activityTypeId: ActivityTypes) {
            return self.rows.some((r) => r.activityTypeId === activityTypeId);
        },
    }));

export type OrderBreakdownStorage = Instance<typeof OrderBreakdownStorageModel>;

export const orderBreakdownStorage = OrderBreakdownStorageModel.create(
    { autoLoad: false },
    { api }
);
